import React, { useMemo, useState, useEffect } from "react"
import styled from "styled-components"
import _clamp from "lodash/clamp"
import chroma, { Color } from "chroma-js"
import { SvgIconProps, useTheme, TypographyVariant } from "@mui/material"
import { scaleLinear } from "d3-scale"
import { isValidNumber } from "../../utils/numbers"

import {
  Needle,
  WrappedIcon,
  CountTypography,
  ScaledTypography
} from "./Gauge.components"

import Sector from "../Sector"
import { getLineCoordinates } from "../../utils/trig"
import { GaugeWrap, LabelRow, ValueWrap } from "./Gauge.styles"

const Svg = styled.svg`
  position: absolute;
`

type TSize = "small" | "medium" | "large" | "xlarge"

interface ISection {
  size: number
  label?: string | number
  color?: string | Color
}

interface IGaugeProps {
  /**
   * The current value
   * @default 0
   */
  value: number
  /**
   * Array of min and max to which the value will be compared to [min, max] where min < max
   * @default [0, 10]
   */
  domain?: number[]
  /**
   * The number of sections that will be displayed. Alternatively, it accepts an array of objects with a value property within. The section will be sized proportionally to the domain
   * @default 3
   */
  sections?: number | ISection[]
  /**
   * Inverted colorScale style
   * @default false
   */
  inverted?: boolean
  /**
   * An icon component to be rendered below the gauge. Material UI icon components are recommended
   */
  icon?: React.FunctionComponent<SvgIconProps>
  /**
   * The size of the component
   * @default "medium"
   */
  size?: TSize
  /**
   * Label for the component
   * @default ""
   */
  label?: string
  /**
   * An array of colors or ColorBrewer string. If more than two colors are in the array, they will be evenly distributed along the gradient
   * @default theme.colorScales.warningInverted.colors(3)
   */
  colorScale?: Color[]
  /**
   * Optional click handler
   */
  onClick?: React.EventHandler<any>
  /**
   * Animate the needle and count animation
   * @default false
   */
  animated?: boolean
  /**
   * Prefix to be applied to the main value. Example: '$'
   * @default ""
   */
  prefix?: string
  /**
   * Suffix to be applied to the main value. Example: 'M'
   * @default ""
   */
  suffix?: string
  /**
   * The threshold the value must be BELOW to, to display the icon
   *
   */
  iconThreshold?: number
  /**
   * Invert the iconThreshold so it displays the icon if the value is ABOVE or EQUAL to the threshold
   * @default false
   */
  invertIconThreshold?: boolean
}

interface ISectionAngles {
  startAng: number
  endAng: number
  color: string
}

interface IPrefixSuffixProps {
  size: TSize
  min: number
  scale: number
  variant: TypographyVariant
  addStyles: { [key: string]: any }
}

const sizeOptions: { [key in TSize]: number } = {
  small: 180,
  medium: 250,
  large: 300,
  xlarge: 400
}

const RANGE = [-120, 120]
const NEEDLE_PADDING = 1.075
const OUTER_ARC_PADDING = 1.05

export const Gauge = ({
  onClick,
  value = 0,
  label = "",
  size = "medium",
  inverted = false,
  domain = [0, 10],
  animated = false,
  icon: Icon,
  iconThreshold,
  invertIconThreshold,
  prefix: _prefix = "",
  suffix = "",
  sections: _sections = 3,
  colorScale = []
}: IGaugeProps) => {
  const theme = useTheme()
  const { palette } = theme
  const strokeColor = palette.text.primary

  const activeColorScale = colorScale.length
    ? colorScale
    : theme.colorScales.warningInverted.colors(3)

  const [min, max] = domain
  const [clampedValue] = useClampedValue(value, min, max)
  const [needleValue, setNeedleValue] = useState(clampedValue)

  // simple validation for JS users
  if (domain.filter(v => Number.isNaN(v)).length) {
    return <p>Invalid domain</p>
  }

  if (Number.isNaN(value)) {
    return <p>Invalid value</p>
  }

  const width = sizeOptions[size]
  const innerRadius = width / 6
  const outerRadius = width / 3
  const viewBox = `
    ${-width / 2} ${-width / 2} ${width} ${width}
  `

  // get number of decimal places in value passed
  const decimalPlaces = getDecimalPlaces(clampedValue) || 0

  // only run animation if the animated flag is passed and the value is a number
  const isAnimated = animated && typeof value === "number"

  // update needleValue in case the needle isn't animated. If the needle is animated, the update is handled by the handleNeedleValue func
  useEffect(() => {
    if (isAnimated) {
      return
    }

    setNeedleValue(clampedValue)
  }, [clampedValue, isAnimated])

  /* SECTIONS */
  // these angles are used by the outerSections, innerSections, and ticks. this calculation, outerSections, nor ticks change when the value changes,
  // but innerSections do (since we need to update the color when the value changes). because of this, we memoize this hefty calc and
  // expose what is necessary for both outerSections and innerSections to render
  const sectionAngles = useMemo(() => {
    const domainDiff = max - min
    const isValidArray =
      Array.isArray(_sections) &&
      _sections.reduce((prev, curr) => prev + curr.size, 0) === domainDiff

    const isNum = typeof _sections === "number"
    const isSectionsValid = isValidArray || isNum

    if (!isSectionsValid) {
      console.warn(
        `Sections must be a number or an array of objects with values that add up to the difference between domain min and domain max (max - min)`
      )
      return []
    }

    // sections takes either a number of sections (which are divided equally) or an array of sections that can be divided
    // if an array is passed, we leave sections as is, if not, we create an array with the relevant properties
    const sections: ISection[] = Array.isArray(_sections)
      ? _sections
      : Array(_sections)
          .fill(null)
          .map(_ => ({ size: domainDiff / _sections }))

    const getScaledSections = scaleLinear([0, domainDiff], RANGE)
    const sectionThetas = sections.map(({ size }) => getScaledSections(size))
    const numberOfSections = sections.length
    const baseline = RANGE[0]

    // invert colorScale array if the inverted flag is passed
    const _colorScale =
      inverted && Array.isArray(activeColorScale)
        ? [...activeColorScale].reverse()
        : activeColorScale

    const getSectionColor = chroma
      .scale(_colorScale)
      .domain([0, numberOfSections - 1])

    let startAng = baseline

    const _sectionAngles: ISectionAngles[] = sectionThetas.map((t, idx) => {
      const endAng = startAng + (Math.abs(baseline) + t)
      const { color: assignedColor } = sections[idx]

      // the developer can override a section color by passing a specific color to the section object within the section array
      // first we check if a color has been assigned by the developer, and if not, it is assigned by the index
      const color = assignedColor
        ? chroma(assignedColor).hex()
        : getSectionColor(idx).hex()

      const _sectionAngle = { startAng, endAng, color }

      startAng = endAng

      return _sectionAngle
    })

    return _sectionAngles
  }, [_sections, colorScale, inverted, max, min])

  if (!sectionAngles || !sectionAngles.length) {
    return <p>Invalid section angles</p>
  }

  const outerSections = useMemo(() => {
    if (!sectionAngles) {
      return null
    }

    return sectionAngles.map(({ startAng, endAng, color }, idx) => (
      <Sector
        key={`${startAng}-${endAng}-outer`}
        color={color}
        startAngle={startAng}
        endAngle={endAng}
        innerRadius={outerRadius}
        outerRadius={outerRadius * OUTER_ARC_PADDING}
      />
    ))
  }, [outerRadius, sectionAngles])

  const { innerSections, activeColor } = useMemo(() => {
    // return an empty object so we can destructure the values returned below
    if (!sectionAngles) {
      return {}
    }

    let activeColor
    const getScaledValue = scaleLinear(domain, RANGE)
    const maxTheta = getScaledValue(max)

    // there is an edge case in which the value is the same as the threshold of the section, but because of not rounding,
    // valTheta is at 49.99999999999... and the appropriate section isn't active, to solve this issue,
    // we must round valTheta to the adequate number of decimals in the clampedValue
    const roundDecimals = 10 ** decimalPlaces
    const valTheta =
      Math.round(getScaledValue(needleValue) * roundDecimals) / roundDecimals

    const innerSections = sectionAngles.map(
      ({ startAng, endAng, color }, idx) => {
        // we know that the section is active by checking the value's theta vs the section's start and end angle
        const isActiveSection =
          (valTheta >= startAng && valTheta < endAng) ||
          (valTheta >= maxTheta && endAng === maxTheta)

        if (isActiveSection) {
          activeColor = color
        }

        return (
          <Sector
            key={`${startAng}-${endAng}-inner`}
            startAngle={startAng}
            endAngle={endAng}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            color={isActiveSection ? color : strokeColor}
            opacityGradient={isActiveSection ? [0, 0.25] : [0, 0.075]}
          />
        )
      }
    )

    return { innerSections, activeColor }
  }, [
    max,
    domain,
    sectionAngles,
    decimalPlaces,
    needleValue,
    innerRadius,
    outerRadius,
    strokeColor
  ])

  /* TICKS */
  // these are the subtle lines that divide the sections
  const ticks = useMemo(() => {
    if (!sectionAngles) {
      return null
    }

    const ticksThetas: number[] = []

    // since only the center lines are needed, we can use the end angles of every section except the last one
    sectionAngles.forEach(({ endAng }, idx) => {
      if (idx === sectionAngles.length - 1) {
        return
      } else {
        ticksThetas.push(endAng)
      }
    })

    const ticksPos = ticksThetas.map(t =>
      getLineCoordinates(t, innerRadius, outerRadius * OUTER_ARC_PADDING)
    )

    return ticksPos.map(coords => (
      <line
        {...coords}
        key={`${coords.x1}-${coords.x2}-ticks`}
        stroke={palette.background.default}
        strokeWidth={1}
      />
    ))
  }, [outerRadius, innerRadius, palette.background.default, sectionAngles])

  const handleNeedleValue = (value: number) => {
    setNeedleValue(value)
  }

  /* VALUE */
  // this prefix and color is in case the value is above or below the domain
  const valPrefix = value < min ? "<" : max < value ? ">" : ""
  const prefix = `${valPrefix}${_prefix}`

  // set the value to the current activeColor if the value is below the domain min or above the domain max
  const valColor = value < min || max < value ? activeColor : strokeColor

  // this props refer to the actual prefix and suffix passed by the user
  const prefixSuffixProps: IPrefixSuffixProps = {
    size,
    min: 0.6,
    scale: 0.65,
    variant: "body2",
    addStyles: {
      opacity: 0.4,
      color: valColor
    }
  }

  /* ICON */
  // check if all the conditions are met to display the icon
  const isDisplayingIcon = checkIsDisplayingIcon(
    value,
    iconThreshold,
    invertIconThreshold
  )

  return sectionAngles === null ? (
    <GaugeWrap width={width}>
      <h3>*** Gauge component error ***</h3>
    </GaugeWrap>
  ) : (
    <GaugeWrap width={width} onClick={evt => onClick && onClick(evt)}>
      <Svg viewBox={viewBox}>
        <g>{innerSections}</g>
        <g>{outerSections}</g>
        <g>{ticks}</g>

        <g>
          <Needle
            size={size}
            range={RANGE}
            domain={domain}
            color={strokeColor}
            value={needleValue}
            innerRadius={innerRadius}
            outerRadius={outerRadius / NEEDLE_PADDING}
          />
        </g>
      </Svg>

      {Icon && isDisplayingIcon ? (
        <WrappedIcon width={width} color={activeColor} Icon={Icon} />
      ) : null}

      <ValueWrap width={width / 3}>
        <ScaledTypography {...prefixSuffixProps}>{prefix}</ScaledTypography>
        {isAnimated ? (
          <CountTypography
            size={size}
            color={valColor}
            value={clampedValue}
            decimalPlaces={decimalPlaces}
            onChangeFn={handleNeedleValue}
          />
        ) : (
          <ScaledTypography
            size={size}
            min={0.8}
            addStyles={{ color: valColor }}
          >
            {clampedValue}
          </ScaledTypography>
        )}
        <ScaledTypography {...prefixSuffixProps}>{suffix}</ScaledTypography>
      </ValueWrap>

      <LabelRow width={width} height={width / 6}>
        <ScaledTypography size={size}>{label}</ScaledTypography>
      </LabelRow>
    </GaugeWrap>
  )
}

// i had to type this hook to tame the ts compiler...
function useClampedValue(
  defaultVal: number = 0,
  min: number,
  max: number
): [number, (value: number) => void] {
  const [value, setValue] = useState(defaultVal)

  useEffect(() => setValue(defaultVal), [defaultVal])

  const clampedValue = useMemo(() => _clamp(value, min, max), [value, min, max])

  return [clampedValue, setValue]
}

function getDecimalPlaces(value: number) {
  if (isValidNumber(value)) {
    return Math.floor(value) === value ? 0 : String(value).split(".")[1].length
  }
}

function checkIsDisplayingIcon(
  value: number,
  iconThreshold?: number,
  inverted?: boolean
) {
  // if there is no threshold, this means the icon will be displaying always, regardless of the value
  if (!iconThreshold) {
    return true
    // else if there is a threshold that ISN'T inverted (below threshold),
    // and the value is below the threshold, the icon will be displayed
  } else if (iconThreshold && !inverted && value < iconThreshold) {
    return true
    // else if there is a threshold that IS inverted (above or equal to threshold),
    // and the value is above/equal to the threshold, the icon will be displayed
  } else if (iconThreshold && inverted && value >= iconThreshold) {
    return true
  }

  // if we got this far, the icon will not be displayed
  return false
}

export default Gauge
